/**
 * TeamsWebhookField — URL input + server-side test button for the Teams
 * incoming-webhook configured on the Settings page (NC-505 Phase 5 PR-C).
 *
 * The field mirrors the backend hostname whitelist (user-settings PUT) so
 * obviously-wrong URLs are flagged before a round-trip; the PUT remains the
 * authoritative validation. The Test button is enabled only when the saved
 * doc already carries a webhook URL and the field is not dirty — the test
 * endpoint always posts to the SAVED URL, never to unsaved input.
 */

import React from 'react';
import { Box, Button, CircularProgress, TextField, Tooltip } from '@mui/material';
import { Send as SendIcon } from '@mui/icons-material';

// Keep in sync with WEBHOOK_HOST_PATTERNS in web-portal/api/user-settings/index.ts.
export function isAllowedWebhookUrl(value: string): boolean {
  let parsed: URL;
  try {
    parsed = new URL(value);
  } catch {
    return false;
  }
  if (parsed.protocol !== 'https:') return false;
  const host = parsed.hostname.toLowerCase();
  return (
    host === 'outlook.office.com' ||
    host.endsWith('.webhook.office.com') ||
    host.endsWith('.logic.azure.com') ||
    host.endsWith('.workflow.microsoft.com')
  );
}

interface TeamsWebhookFieldProps {
  value: string;
  onChange: (value: string) => void;
  onTest: () => void;
  /** True while the test POST is in flight. */
  testing: boolean;
  /** True when the field differs from the saved doc — test posts the SAVED URL only. */
  dirty: boolean;
  /** True when the saved doc carries a webhook URL. */
  savedUrlPresent: boolean;
  disabled?: boolean;
}

export default function TeamsWebhookField({
  value,
  onChange,
  onTest,
  testing,
  dirty,
  savedUrlPresent,
  disabled,
}: TeamsWebhookFieldProps) {
  const showInvalid = value.trim() !== '' && !isAllowedWebhookUrl(value.trim());
  const testDisabled = testing || !savedUrlPresent || dirty;
  const testTooltip = !savedUrlPresent
    ? 'Zuerst eine Webhook-URL speichern'
    : dirty
      ? 'Ungespeicherte Änderung — erst speichern, dann testen'
      : 'Test-Karte an den gespeicherten Webhook senden';

  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
      <TextField
        fullWidth
        label="Teams Webhook-URL"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        error={showInvalid}
        helperText={
          showInvalid
            ? 'URL muss https sein und auf einen Microsoft-Teams- / Power-Automate-Endpoint zeigen'
            : 'Incoming-Webhook aus Teams (Connectors) oder Power-Automate-Workflow; leer = deaktiviert'
        }
        placeholder="https://….webhook.office.com/webhookb2/…"
      />
      <Tooltip title={testTooltip}>
        <span>
          <Button
            variant="outlined"
            onClick={onTest}
            disabled={testDisabled}
            startIcon={testing ? <CircularProgress size={16} /> : <SendIcon />}
            sx={{ mt: 1, whiteSpace: 'nowrap' }}
          >
            Test
          </Button>
        </span>
      </Tooltip>
    </Box>
  );
}
